import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'flowship-theme-mode';

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'light',
  toggleMode: () => undefined,
});

const getInitialMode = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';

  try {
    const savedMode = window.localStorage.getItem(STORAGE_KEY);
    if (savedMode === 'light' || savedMode === 'dark') return savedMode;
  } catch {
    // Storage may be unavailable in hardened/private browser contexts.
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const ThemeModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
    document.querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', mode === 'dark' ? '#0b1220' : '#ffffff');
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Applying the theme must still work when persistence is unavailable.
    }
  }, [mode]);

  const value = useMemo<ThemeModeContextValue>(() => ({
    mode,
    toggleMode: () => setMode((currentMode) => currentMode === 'light' ? 'dark' : 'light'),
  }), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextValue => {
  return useContext(ThemeModeContext);
};
